// src/features/account/components/AddPaymentMethodModal.jsx
// Modal for adding a payment method (credit card) without charging
import React, { useState, useEffect } from 'react';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { X, CreditCard, Shield, AlertCircle } from 'lucide-react';
import { useWorkOS } from '../../workos/context/WorkOSContext';

// Initialize Stripe outside component to avoid recreating
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

const API_BASE = '/.netlify/functions';

// Inner form component (needs Stripe context)
const PaymentMethodForm = ({ onSuccess, onCancel, context }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { getValidAccessToken } = useWorkOS();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Detect if Stripe fails to load
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!stripe || !elements || !cardReady) {
        setLoadError('Payment form failed to load. Please try opening this page in incognito/private mode.');
      }
    }, 5000);
    return () => clearTimeout(timeout);
  }, [stripe, elements, cardReady]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    try {
      // Step 1: Get SetupIntent from backend
      const accessToken = await getValidAccessToken();
      const setupResponse = await fetch(`${API_BASE}/add-payment-method`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!setupResponse.ok) {
        const errorData = await setupResponse.json();
        throw new Error(errorData.error || 'Failed to initialize payment setup');
      }

      const { clientSecret } = await setupResponse.json();

      // Step 2: Confirm SetupIntent with card details
      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        }
      });

      if (confirmError) {
        throw confirmError;
      }

      if (setupIntent.status === 'succeeded') {
        onSuccess();
      } else {
        throw new Error('Card setup did not complete successfully');
      }

    } catch (err) {
      console.error('Payment method setup error:', err);
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const contextMessages = {
    download: {
      title: 'No Credits Available',
      subtitle: 'Add a payment method to purchase credits',
      description: 'Your card will only be charged when you purchase credits.',
      showCreditsLink: true
    },
    upload: {
      title: 'Verify Your Account',
      subtitle: 'Required to publish on the Exchange',
      description: 'Verified publishers help keep the Exchange safe for everyone. Your card will not be charged.',
      showCreditsLink: false
    },
    upgrade: {
      title: 'Add Payment Method',
      subtitle: 'Required to subscribe to Pro',
      description: 'Your card will be charged for the Pro subscription after adding.',
      showCreditsLink: false
    },
    default: {
      title: 'Add Payment Method',
      subtitle: 'Verify your account',
      description: 'Add a payment method to access all Exchange features.',
      showCreditsLink: false
    }
  };

  const messages = contextMessages[context] || contextMessages.default;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl max-w-md w-full p-6 relative">
        {/* Close button */}
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600/20 rounded-full mb-4">
            <CreditCard className="h-6 w-6 text-blue-500" />
          </div>
          <h2 className="text-xl font-semibold text-white">{messages.title}</h2>
          <p className="text-gray-400 text-sm mt-1">{messages.subtitle}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {loadError && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500 rounded-lg">
              <p className="text-yellow-400 text-sm">{loadError}</p>
            </div>
          )}

          <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
            <CardElement
              onReady={() => setCardReady(true)}
              onChange={(event) => {
                if (event.error) {
                  setError(event.error.message);
                } else {
                  setError(null);
                }
              }}
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#ffffff',
                    '::placeholder': {
                      color: '#9ca3af'
                    },
                    iconColor: '#ffffff',
                  },
                  invalid: {
                    color: '#ef4444',
                    iconColor: '#ef4444'
                  }
                },
                hidePostalCode: true
              }}
            />
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Security note */}
          <div className="flex items-center gap-2 text-gray-500 text-xs">
            <Shield className="h-4 w-4" />
            <span>{messages.description}</span>
          </div>

          {/* Alternative: Go to credits page */}
          {messages.showCreditsLink && (
            <div className="text-center text-sm">
              <span className="text-gray-500">or </span>
              <a 
                href="/account?tab=credits" 
                className="text-blue-400 hover:text-blue-300 hover:underline"
                onClick={onCancel}
              >
                add credits via Account page
              </a>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-3 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing || !stripe || !elements}
              className="flex-1 px-4 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? 'Adding...' : 'Add Card'}
            </button>
          </div>
        </form>

        {/* Stripe badge + portal link */}
        <div className="mt-4 text-center space-y-2">
          <span className="text-gray-600 text-xs">Secured by Stripe</span>
          <div>
            <button
              type="button"
              onClick={async () => {
                try {
                  const accessToken = await getValidAccessToken();
                  const response = await fetch(`${API_BASE}/stripe-portal-session`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${accessToken}`
                    }
                  });
                  const data = await response.json();
                  if (data.url) {
                    window.open(data.url, '_blank');
                  } else if (data.error) {
                    alert(data.error);
                  } else {
                    alert('Unable to open Stripe portal.');
                  }
                } catch (err) {
                  console.error('Failed to open Stripe portal:', err);
                  alert('Failed to connect to Stripe.');
                }
              }}
              className="text-blue-400 hover:text-blue-300 text-xs hover:underline"
            >
              Prefer to manage on Stripe directly?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main modal component (wraps form with Stripe Elements provider)
const AddPaymentMethodModal = ({ isOpen, onClose, onSuccess, context = 'default' }) => {
  if (!isOpen) return null;

  const handleSuccess = () => {
    onSuccess?.();
    onClose();
  };

  return (
    <Elements stripe={stripePromise}>
      <PaymentMethodForm
        onSuccess={handleSuccess}
        onCancel={onClose}
        context={context}
      />
    </Elements>
  );
};

export default AddPaymentMethodModal;
